import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ROLE_LABELS, STATUS_STYLES } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import {
  Eye, ShieldCheck, Users, Compass, ArrowRight, ClipboardList, GanttChartSquare,
  LayoutDashboard, UserCircle2, ChevronRight, Lock,
} from "lucide-react";

const PRINCIPLES = [
  { icon: Eye, title: "Transparency", text: "Every finding, mitigation, and status change is visible and traceable — one source of truth instead of scattered decks and inboxes." },
  { icon: ShieldCheck, title: "Accountability", text: "Clear ownership on every workstream, task, and risk, with PMO governance and an append-only audit trail behind every change." },
  { icon: Users, title: "Inclusion", text: "Executives, PMO, workstream owners, and engineers each get the view relevant to their role — everyone contributes to the plan." },
];

const PHASES = [
  { n: 1, title: "Assess", text: "Structured current-state review of each workstream against the target operating model." },
  { n: 2, title: "Plan", text: "Turn findings into linked mitigation actions, tasks, milestones, and budget." },
  { n: 3, title: "Mitigate", text: "Execute the plan, track progress, and escalate risks through PMO governance." },
  { n: 4, title: "Stabilize", text: "Operationalize changes and confirm outcomes before the next strategy cycle." },
];

const QUICK = {
  executive: [{ to: "/dashboard", label: "Open Executive Dashboard", icon: LayoutDashboard }, { to: "/reports", label: "View Reports", icon: ClipboardList }],
  pmo: [{ to: "/pmo", label: "PMO Control Center", icon: ShieldCheck }, { to: "/project-plan", label: "Project Plan", icon: GanttChartSquare }],
  it_admin: [{ to: "/admin/users", label: "Manage Users", icon: Users }, { to: "/admin/audit-log", label: "Audit Log", icon: ClipboardList }],
  it_executive: [{ to: "/dashboard", label: "My Workstream Dashboard", icon: LayoutDashboard }, { to: "/my-responsibility", label: "My Responsibility", icon: UserCircle2 }],
  engineer: [{ to: "/my-responsibility", label: "My Tasks", icon: UserCircle2 }],
};

export default function Home() {
  const { user, effectiveRole } = useAuth();
  const nav = useNavigate();
  const [rollup, setRollup] = useState(null);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("portalPinOk") === "1");
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState(false);

  useEffect(() => { api.get("/dashboard").then((r) => setRollup(r.data.rollup)).catch(() => {}); }, []);
  const quick = QUICK[effectiveRole] || [];

  const submitPin = async (e) => {
    e?.preventDefault();
    try {
      const res = await api.post("/verify-pin", { pin });
      if (res.data.ok) { sessionStorage.setItem("portalPinOk", "1"); setUnlocked(true); setPinErr(false); }
      else setPinErr(true);
    } catch { setPinErr(true); }
  };

  if (!unlocked) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="pin-gate">
        <form onSubmit={submitPin} className="bg-white rounded-[18px] border border-[#DCE2EA] p-8 lg:p-10 max-w-md w-full text-center shadow-[0_10px_40px_rgba(31,39,51,0.08)]">
          <div className="h-14 w-14 rounded-2xl bg-[#FBEAED] text-[#BA0C2F] flex items-center justify-center mx-auto"><Lock className="h-7 w-7" /></div>
          <h1 className="mt-5 text-2xl font-extrabold text-[#1F2733]">Enter access code</h1>
          <p className="mt-2 text-sm text-[#5A687C]">This portal is restricted. Enter the 4-digit access code to continue.</p>
          <input data-testid="pin-input" value={pin} onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setPinErr(false); }}
            inputMode="numeric" maxLength={4} autoFocus placeholder="••••"
            className={`mt-6 w-40 mx-auto block text-center text-3xl tracking-[0.4em] font-bold py-3 rounded-xl border ${pinErr ? "border-[#BA0C2F]" : "border-[#DCE2EA]"} focus:border-[#BA0C2F] outline-none`} />
          {pinErr && <div data-testid="pin-error" className="mt-3 text-sm text-[#BA0C2F] font-semibold">Incorrect code. Please try again.</div>}
          <button data-testid="pin-submit-btn" type="submit" className="mt-6 w-full bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full py-3 font-semibold transition-colors">Unlock portal</button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-[18px] bg-[#BA0C2F] text-white p-8 lg:p-12">
        <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-white/5" />
        <div className="relative max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold mb-4">
            <Compass className="h-3.5 w-3.5" /> IT Digital Transformation Program
          </div>
          <h1 className="text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            Welcome, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="mt-4 text-white/85 text-lg leading-relaxed">
            This is the single system of record for Carle Health IT's transformation. Our goal is to
            <strong className="text-white"> develop a strategy that aligns our workstreams and mitigates risk to prepare us for the future</strong> —
            replacing disconnected spreadsheets and slide decks with one trustworthy, real-time portal.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {quick.map((q) => {
              const Icon = q.icon;
              return (
                <button key={q.to} data-testid={`home-quick-${q.to.replace(/\W+/g, "-")}`} onClick={() => nav(q.to)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#BA0C2F] font-semibold hover:bg-white/90 transition-all">
                  <Icon className="h-4 w-4" /> {q.label} <ArrowRight className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Executive sponsor quote */}
      <section className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 lg:p-8" data-testid="sponsor-quote">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
          <div className="shrink-0 text-center">
            <img src="https://customer-assets-jai6qajn.emergentagent.net/job_transform-dashboard-2/artifacts/8rjx2t8r_images.jpg"
              alt="Steven Ramirez" className="h-28 w-28 rounded-2xl object-cover border-4 border-[#F4F6FA] shadow-md mx-auto" />
            <div className="mt-3 font-bold text-[#1F2733]">Steven Ramirez</div>
            <div className="text-xs text-[#5A687C]">Executive Sponsor</div>
          </div>
          <blockquote className="relative flex-1">
            <span className="absolute -left-1 -top-7 text-6xl text-[#BA0C2F]/20 font-serif leading-none select-none">&ldquo;</span>
            <p className="text-[#1F2733] text-base lg:text-lg leading-relaxed italic">
              From a CIO's perspective, an assessment is essential because you can't effectively improve what you haven't
              measured. It gives us a clear understanding of our current state, identifies risks and gaps, and helps ensure
              our technology investments are focused on the areas that matter most. More importantly, it provides the
              foundation for a strategic roadmap that aligns technology, cybersecurity, and operational priorities with the
              organization's overall mission.
            </p>
            <footer className="mt-3 text-sm font-bold text-[#BA0C2F]">— Steven Ramirez</footer>
          </blockquote>
        </div>
      </section>

      {/* Why this matters */}
      <section>
        <h2 className="text-xl lg:text-2xl font-bold text-[#1F2733]">Why this matters</h2>
        <p className="mt-2 text-[#5A687C] max-w-3xl leading-relaxed">
          The next IT strategy cycle depends on a shared, honest picture of where we are today and a disciplined plan to
          close the gaps. This portal exists to make that picture <strong className="text-[#1F2733]">transparent</strong>,
          hold the program <strong className="text-[#1F2733]">accountable</strong>, and keep every stakeholder
          <strong className="text-[#1F2733]"> included</strong> — the foundation for the organization's success.
        </p>
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {PRINCIPLES.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} data-testid={`principle-${p.title.toLowerCase()}`}
                className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 shadow-[0_2px_10px_rgba(31,39,51,0.04)] hover:shadow-[0_12px_32px_rgba(56,96,190,0.12)] hover:-translate-y-0.5 transition-all">
                <div className="h-11 w-11 rounded-xl bg-[#FBEAED] text-[#BA0C2F] flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <div className="mt-4 font-bold text-[#1F2733]">{p.title}</div>
                <p className="mt-1.5 text-sm text-[#5A687C] leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* The process */}
      <section>
        <h2 className="text-xl lg:text-2xl font-bold text-[#1F2733]">How the program works</h2>
        <p className="mt-2 text-[#5A687C] max-w-3xl leading-relaxed">
          Every workstream moves through four phases. Each owner leads a structured review, findings are mapped to
          mitigation actions, and the PMO governs status and publishing so executives always see a current, trustworthy plan.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PHASES.map((p) => (
            <div key={p.n} className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 relative">
              <div className="h-9 w-9 rounded-full bg-[#BA0C2F] text-white flex items-center justify-center font-bold">{p.n}</div>
              <div className="mt-3 font-bold text-[#1F2733]">{p.title}</div>
              <p className="mt-1.5 text-sm text-[#5A687C] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Program snapshot */}
      {rollup && (
        <section className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#1F2733]">Program snapshot</h2>
            <button onClick={() => nav("/assessment")} className="text-sm font-semibold text-[#BA0C2F] inline-flex items-center gap-1 hover:underline">
              View all workstreams <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(rollup).map(([status, count]) => (
              <div key={status} className="rounded-2xl border p-4" style={{ borderColor: STATUS_STYLES[status].dot + "40" }}>
                <div className="text-3xl font-extrabold" style={{ color: STATUS_STYLES[status].dot }}>{count}</div>
                <div className="mt-1"><StatusBadge status={status} /></div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
